import { type Post, type Topic, type TopicSection } from '@prisma/client'
import {
	json,
	Link,
	Outlet,
	useLoaderData,
	useParams,
	useRouteLoaderData,
} from '@remix-run/react'
import { type LoaderFunctionArgs } from '@remix-run/server-runtime'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import PageTitle from '#app/components/pageLayout/PageTitle.tsx'
import { Breadcrumb } from '#app/components/tramite/breadcrumb.tsx'
import { findTopicSectionBySlug } from '#app/routes/_appAdmin+/topicSections/_topicSectionQueries.server.ts'
import { invariantResponse } from '#app/utils/misc.tsx'

export async function loader({ params }: LoaderFunctionArgs) {
	const topicSection: Partial<TopicSection> & {
		topic: Partial<Topic>
	} & { posts: Partial<Post>[] } = await findTopicSectionBySlug(
		params.topicSection!,
		params.topic!,
		{
			id: true,
			name: true,
			slug: true,
			description: true,
			posts: {
				select: {
					id: true,
					name: true,
					slug: true,
				},
			},
		},
	)

	invariantResponse(topicSection, 'Sección no encontrada', { status: 404 })
	return json({
		topicSection,
	})
}

export default function TopicSectionRoute() {
	const { topicSection: topicSectionParam, post: postParam } = useParams()
	const { topic } = useRouteLoaderData(
		'routes/_publicWithSidebar+/$country+/temas.$topic',
	)
	const { topicSection } = useLoaderData<typeof loader>()

	const pages = [
		{ name: 'Temas', href: `/${topic.country.code}/temas`, current: false },
		{
			name: topic.name,
			href: `/${topic.country.code}/temas/${topic.slug}`,
			current: false,
		},
		{
			name: topicSection.name,
			href: '#',
			current: true,
		},
	].filter(Boolean)

	return (
		<>
			{topicSectionParam && !postParam ? (
				<>
					<div className="mx-auto w-full min-w-0 max-w-4xl px-8">
						<div>
							<Breadcrumb pages={pages} />
							<PageTitle
								title={topicSection.name}
								sizeClasses="text-3xl sm:text-2xl lg:text-3xl"
							/>
							<div className="prose ">
								{topicSection?.description &&
									topicSection?.description
										.split('\n\n')
										.map((paragraph, index) => (
											<p key={index}>{paragraph}</p>
										))}{' '}
								<ul>
									{topicSection.posts.map(post => (
										<li key={post.id}>
											<Link
												to={`/${topic.country.code}/temas/${topic.slug}/${topicSection.slug}/${post.slug}`}
												prefetch="intent"
												reloadDocument
											>
												{post.name}
											</Link>
										</li>
									))}
								</ul>
							</div>
						</div>
					</div>
					<div className="page-nav sticky top-[6.5rem] -ml-10 mr-10 hidden max-h-[calc(100vh-theme('spacing.36'))] w-64 flex-none self-start text-sm 2xl:block">
						{' '}
					</div>
				</>
			) : (
				<Outlet />
			)}
		</>
	)
}

export function ErrorBoundary() {
	return (
		<GeneralErrorBoundary
			statusHandlers={{
				403: () => <p>No tienes permiso para hacer eso</p>,
				404: ({ params }) => <p>Este contenido no existe en Papelea</p>,
			}}
		/>
	)
}
